import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import { api, type Client, type CreateClientPayload, type UpdateClientPayload } from '../../utils/api';

export type RequestStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface ClientsListState {
  data: Client[];
  status: RequestStatus;
  error: string | null;
}

interface ClientDetailState {
  data: Client | null;
  status: RequestStatus;
  error: string | null;
}

interface ClientsState {
  list: ClientsListState;
  detail: ClientDetailState;
}

const initialState: ClientsState = {
  list: {
    data: [],
    status: 'idle',
    error: null,
  },
  detail: {
    data: null,
    status: 'idle',
    error: null,
  },
};

const getErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const responseMessage =
      typeof error.response?.data === 'object'
        ? (error.response?.data as { message?: string })?.message
        : undefined;
    return responseMessage ?? error.message ?? 'Something went wrong.';
  }
  return error instanceof Error ? error.message : 'Something went wrong.';
};

export const fetchClientsThunk = createAsyncThunk<Client[], void, { rejectValue: string }>(
  'clients/fetchList',
  async (_, thunkAPI) => {
    try {
      const response = await api.get<Client[]>('/clients');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const fetchClientByIdThunk = createAsyncThunk<Client, string, { rejectValue: string }>(
  'clients/fetchById',
  async (clientId, thunkAPI) => {
    try {
      const response = await api.get<Client>(`/clients/${clientId}`);
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const createClientThunk = createAsyncThunk<Client, CreateClientPayload, { rejectValue: string }>(
  'clients/create',
  async (payload, thunkAPI) => {
    try {
      const response = await api.post<Client>('/clients', payload);
      toast.success('Client added successfully.');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const updateClientThunk = createAsyncThunk<Client, { clientId: string; payload: UpdateClientPayload }, { rejectValue: string }>(
  'clients/update',
  async ({ clientId, payload }, thunkAPI) => {
    try {
      const response = await api.put<Client>(`/clients/${clientId}`, payload);
      toast.success('Client updated successfully.');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const deactivateClientThunk = createAsyncThunk<string, string, { rejectValue: string }>(
  'clients/deactivate',
  async (clientId, thunkAPI) => {
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Client deactivated successfully.');
      return clientId;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const activateClientThunk = createAsyncThunk<Client, string, { rejectValue: string }>(
  'clients/activate',
  async (clientId, thunkAPI) => {
    try {
      const response = await api.patch<Client>(`/clients/${clientId}/activate`);
      toast.success('Client reactivated successfully.');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    setClientsFromCache(state, action: PayloadAction<Client[]>) {
      state.list.data = action.payload;
      state.list.status = 'succeeded';
      state.list.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientsThunk.pending, (state) => {
        state.list.status = 'loading';
        state.list.error = null;
      })
      .addCase(fetchClientsThunk.fulfilled, (state, action) => {
        state.list.status = 'succeeded';
        state.list.data = action.payload;
      })
      .addCase(fetchClientsThunk.rejected, (state, action) => {
        state.list.status = 'failed';
        state.list.error = action.payload ?? 'Unable to load clients.';
      })
      .addCase(fetchClientByIdThunk.pending, (state) => {
        state.detail.status = 'loading';
        state.detail.error = null;
      })
      .addCase(fetchClientByIdThunk.fulfilled, (state, action) => {
        state.detail.status = 'succeeded';
        state.detail.data = action.payload;
        const index = state.list.data.findIndex((client) => client._id === action.payload._id);
        if (index !== -1) {
          state.list.data[index] = action.payload;
        }
      })
      .addCase(fetchClientByIdThunk.rejected, (state, action) => {
        state.detail.status = 'failed';
        state.detail.error = action.payload ?? 'Unable to load client.';
      })
      .addCase(createClientThunk.fulfilled, (state, action) => {
        state.list.data = [action.payload, ...state.list.data];
        state.list.status = 'succeeded';
      })
      .addCase(createClientThunk.rejected, (state, action) => {
        state.list.error = action.payload ?? 'Unable to create client.';
      })
      .addCase(updateClientThunk.fulfilled, (state, action) => {
        state.detail.status = 'succeeded';
        state.detail.data = action.payload;
        const index = state.list.data.findIndex((client) => client._id === action.payload._id);
        if (index !== -1) {
          state.list.data[index] = action.payload;
        }
      })
      .addCase(updateClientThunk.rejected, (state, action) => {
        state.detail.status = 'failed';
        state.detail.error = action.payload ?? 'Unable to update client.';
      })
      .addCase(deactivateClientThunk.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter((client) => client._id !== action.payload);
        if (state.detail.data?._id === action.payload) {
          state.detail.data = null;
          state.detail.status = 'idle';
        }
      })
      .addCase(activateClientThunk.fulfilled, (state, action) => {
        const index = state.list.data.findIndex((client) => client._id === action.payload._id);
        if (index !== -1) {
          state.list.data[index] = action.payload;
        } else {
          state.list.data = [action.payload, ...state.list.data];
        }
        if (state.detail.data?._id === action.payload._id) {
          state.detail.data = action.payload;
          state.detail.status = 'succeeded';
        }
      });
  },
});

export const { setClientsFromCache } = clientsSlice.actions;
export default clientsSlice.reducer;
